import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Stack, Paper, Grid, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemAvatar, ListItemText, Avatar, Checkbox, CircularProgress
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { uploadShopImage, deleteMultipleFromR2 } from '../../utils/r2Upload';
import { compressShopImage } from '../../utils/imageCompression';
import { KeywordSelector } from '../../components/KeywordSelector';



// URL을 File 객체로 변환하는 함수
const urlToFile = async (url: string, filename: string): Promise<File> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  } catch (error) {
    console.error('URL을 File로 변환 실패:', error);
    throw error;
  }
};

// 상품 타입 정의
interface Product {
  product_id: number;
  product_name: string;
  price: number;
  main_images: string[];
  keywords: string[];
  description: string;
  store_name: string;
}

export default function Shop() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const navigate = useNavigate();

  // 저장 중 로딩 상태 추가
  const [isSaving, setIsSaving] = useState(false);
  
  // 미리보기 데이터 상태
  const [previewData, setPreviewData] = useState({
    brandName: '브랜드A',
    brandDescription: '감각적인 미니멀리즘 브랜드',
    promotionText: '6월 한정 신상품 10% 할인 이벤트 진행 중!',
    pageTitle: 'Ex cum matis',
    selectedTab: 'product',
    selectedSubTab: '탭 1',
    weeklyBestTitle: 'Weekly Best',
    subTabNames: {
      '탭 1': '탭 1',
      '탭 2': '탭 2',
      '탭 3': '탭 3',
      '탭 4': '탭 4'
    },
    images: {
      logo: [] as string[],    // 로고 이미지 추가
      main: [] as string[],
      sub1: [] as string[],
      sub2: [] as string[]
    }
  });

  // 이미지 파일 상태
  const [imageFiles, setImageFiles] = useState({
    logo: [] as File[],    // 로고 이미지 파일 추가
    main: [] as File[],
    sub1: [] as File[],
    sub2: [] as File[]
  });

  // 기존 이미지 URL 추적 (수정 시 삭제 감지용)
  const [existingImageUrls, setExistingImageUrls] = useState({
    logo: [] as string[],
    main: [] as string[],
    sub1: [] as string[],
    sub2: [] as string[]
  });

  // 원본 이미지 URL (삭제 감지용)
  const [originalImageUrls, setOriginalImageUrls] = useState({
    logo: [] as string[],
    main: [] as string[],
    sub1: [] as string[],
    sub2: [] as string[]
  });

  // 드래그 상태
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [detailDraggingIdx, setDetailDraggingIdx] = useState<number | null>(null);
  const [detailDragOverIdx, setDetailDragOverIdx] = useState<number | null>(null);

  // 좋아요 상태 관리
  const [isLiked, setIsLiked] = useState(false);

  // 이미지 슬라이드 인덱스 상태
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [sub1ImageIndex, setSub1ImageIndex] = useState(0);
  const [sub2ImageIndex, setSub2ImageIndex] = useState(0);

  // 상품 관련 상태
  const [products, setProducts] = useState<Product[]>([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]); // 검색 키워드 상태
  const [subTabProducts, setSubTabProducts] = useState<{
    [key: string]: Product[]
  }>({
    '탭 1': [],
    '탭 2': [],
    '탭 3': [],
    '탭 4': []
  });

  // 이미지 업로드 핸들러 (AddProducts.tsx와 동일한 방식)
  const handleImageChange = (type: 'logo' | 'main' | 'sub1' | 'sub2') => {
    const input = document.createElement('input');
    input.type = 'file';
    
    // 로고는 1장만, 나머지는 여러장
    if (type === 'logo') {
      input.multiple = false;
    } else {
      input.multiple = true;
    }
    
    input.accept = 'image/png,image/jpg,image/jpeg';
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);

      // 파일 형식 검증 (AddProducts.tsx와 동일)
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

      if (invalidFiles.length > 0) {
        alert('PNG, JPG, JPEG 형식의 이미지만 업로드 가능합니다.');
        return;
      }

      // 이미지 압축
      console.log(`🔄 ${files.length}개 이미지 압축 시작...`);
      const compressedFiles = await Promise.all(
        files.map(file => compressShopImage(file))
      );
      console.log(`✅ ${compressedFiles.length}개 이미지 압축 완료`);

      let newFiles: File[];
      if (type === 'logo') {
        // 로고는 1장만 허용
        newFiles = compressedFiles.slice(0, 1);
      } else {
        // 나머지는 최대 5장
        newFiles = [...imageFiles[type], ...compressedFiles].slice(0, 5);
      }
      
      setImageFiles(prev => ({ ...prev, [type]: newFiles }));
      setPreviewData(prev => ({
        ...prev,
        images: { 
          ...prev.images, 
          [type]: newFiles.map(f => URL.createObjectURL(f))
        }
      }));
    };
    input.click();
  };

  // 이미지 제거 핸들러
  const handleRemoveImage = (type: 'logo' | 'main' | 'sub1' | 'sub2', idx: number) => {
    // 기존 URL에서 제거하는 경우 (File이 아닌 URL인 경우)
    const totalExistingCount = existingImageUrls[type].length;

    if (idx < totalExistingCount) {
      // 기존 URL 제거
      setExistingImageUrls(prev => ({
        ...prev,
        [type]: prev[type].filter((_, i) => i !== idx)
      }));
    } else {
      // 새 File 제거
      const fileIdx = idx - totalExistingCount;
      setImageFiles(prev => ({
        ...prev,
        [type]: prev[type].filter((_, i) => i !== fileIdx)
      }));
    }

    // 미리보기 데이터 업데이트
    const updatedExistingUrls = idx < totalExistingCount
      ? existingImageUrls[type].filter((_, i) => i !== idx)
      : existingImageUrls[type];

    const updatedFiles = idx >= totalExistingCount
      ? imageFiles[type].filter((_, i) => i !== (idx - totalExistingCount))
      : imageFiles[type];

    setPreviewData(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [type]: [...updatedExistingUrls, ...updatedFiles.map(f => URL.createObjectURL(f))]
      }
    }));
  };

  // 텍스트 변경 핸들러
  const handleTextChange = (field: string, value: string) => {
    setPreviewData(prev => ({ ...prev, [field]: value }));
  };

  // 서브 탭 변경 핸들러
  const handleSubTabChange = (subTab: string) => {
    setPreviewData(prev => ({ ...prev, selectedSubTab: subTab }));
  };

  // 좋아요 토글 핸들러
  const handleLikeToggle = () => {
    setIsLiked(!isLiked);
  };

  // 상품 검색 및 로드
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      console.log('=== 상품 데이터 로드 시작 ===');

      // useAuth에서 사용자 정보 가져오기
      if (!user) {
        console.error('❌ 로그인된 사용자 정보가 없습니다.');
        return;
      }
      console.log('✅ 사용자 정보:', user.id);

      // seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        console.error('❌ Seller Profile 정보를 찾을 수 없습니다:', profileError);
        return;
      }

      console.log('✅ Seller Profile ID:', sellerProfile.id);

      // 해당 Seller의 상품만 조회 (seller_profile.id = products.seller_id)
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', sellerProfile.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ 상품 로드 실패:', error);
        return;
      }

      console.log('📦 조회된 상품 개수:', data?.length || 0);
      console.log('📦 상품 목록:', data?.map(p => ({ id: p.product_id, name: p.product_name, price: p.price })));

      setProducts(data || []);
      
      if (!data || data.length === 0) {
        console.log('⚠️ 현재 사용자에게 등록된 상품이 없습니다.');
      }
      
      console.log('✅ 상품 데이터 로드 완료');
    } catch (error) {
      console.error('❌ 상품 로드 중 오류:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // 상품 선택 핸들러
  const handleSelectProduct = (product: Product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.find(p => p.product_id === product.product_id);
      if (isSelected) {
        return prev.filter(p => p.product_id !== product.product_id);
      } else {
        return [...prev, product];
      }
    });
  };

  // 상품 추가 핸들러
  const handleAddProducts = () => {
    const currentSubTab = previewData.selectedSubTab;
    setSubTabProducts(prev => ({
      ...prev,
      [currentSubTab]: [...prev[currentSubTab], ...selectedProducts]
    }));
    setSelectedProducts([]);
    setProductDialogOpen(false);
    setProductSearchTerm('');
  };

  // 상품 제거 핸들러
  const handleRemoveProduct = (subTab: string, productId: number) => {
    setSubTabProducts(prev => ({
      ...prev,
      [subTab]: prev[subTab].filter(p => p.product_id !== productId)
    }));
  };

  // 저장된 데이터 불러오기
  const loadSavedData = async () => {
    try {
      console.log('저장된 데이터 로드 시작');

      // useAuth에서 사용자 정보 가져오기
      if (!user) {
        console.error('로그인된 사용자 정보가 없습니다.');
        return;
      }

      console.log('사용자 ID:', user.id);

      // seller_profile에서 seller 정보 및 store_name 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id, store_name')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        console.error('Seller 정보를 찾을 수 없습니다:', profileError);
        return;
      }

      console.log('Seller Profile ID:', sellerProfile.id);
      console.log('Seller Store Name:', sellerProfile.store_name);

      // seller_store 데이터 가져오기
      const { data: storeData, error: storeError } = await supabase
        .from('seller_store')
        .select('*')
        .eq('seller_id', sellerProfile.id)
        .single();

      if (storeError) {
        console.log('저장된 store 데이터가 없습니다:', storeError);
        // seller_store 데이터가 없어도 seller_profile의 store_name은 사용
        setPreviewData(prev => ({
          ...prev,
          brandName: sellerProfile.store_name || '브랜드A',
          pageTitle: sellerProfile.store_name || 'Ex cum matis',
        }));
        return;
      }

      console.log('Store 데이터:', storeData);

      // 저장된 데이터로 상태 업데이트
      // seller_store의 store_name이 있으면 우선 사용, 없으면 seller_profile의 store_name 사용
      setPreviewData(prev => ({
        ...prev,
        brandName: storeData.store_name || sellerProfile.store_name || '브랜드A',
        brandDescription: storeData.store_description || '감각적인 미니멀리즘 브랜드',
        promotionText: Array.isArray(storeData.promotion_text) ? storeData.promotion_text[0] || '' : storeData.promotion_text || '',
        pageTitle: storeData.store_name || sellerProfile.store_name || 'Ex cum matis',
        weeklyBestTitle: storeData.sub_tab_title || 'Weekly Best',
        images: {
          logo: storeData.logo_image ? [storeData.logo_image] : [], // 로고 이미지 (1장)
          main: storeData.main_image ? storeData.main_image : [],
          sub1: storeData.sub_image_1 ? storeData.sub_image_1 : [],
          sub2: storeData.sub_image_2 ? storeData.sub_image_2 : []
        },
        subTabNames: {
          '탭 1': storeData.sub_tab_1 || '탭 1',
          '탭 2': storeData.sub_tab_2 || '탭 2',
          '탭 3': storeData.sub_tab_3 || '탭 3',
          '탭 4': storeData.sub_tab_4 || '탭 4'
        }
      }));

      // 검색 키워드 로드
      if (storeData.search_keywords && Array.isArray(storeData.search_keywords)) {
        setKeywords(storeData.search_keywords);
        console.log('✅ 검색 키워드 로드:', storeData.search_keywords);
      }

      // 원본 이미지 URL 저장 (삭제 감지용)
      const originalUrls = {
        logo: storeData.logo_image ? [storeData.logo_image] : [],
        main: storeData.main_image ? storeData.main_image : [],
        sub1: storeData.sub_image_1 ? storeData.sub_image_1 : [],
        sub2: storeData.sub_image_2 ? storeData.sub_image_2 : []
      };
      setOriginalImageUrls(originalUrls);
      setExistingImageUrls(originalUrls); // 현재 표시용도 동일하게 설정

      console.log('✅ seller_store에서 서브탭 이름 로드 완료:', {
        sub_tab_1: storeData.sub_tab_1 || '탭 1',
        sub_tab_2: storeData.sub_tab_2 || '탭 2',
        sub_tab_3: storeData.sub_tab_3 || '탭 3',
        sub_tab_4: storeData.sub_tab_4 || '탭 4'
      });

      // 이미지는 existingImageUrls에만 저장됩니다.
      // imageFiles는 새로 업로드되는 파일만 저장합니다.
      console.log('✅ 기존 이미지 URL 로드 완료');

      // store_tab_products 데이터 가져오기
      const { data: tabProducts, error: tabError } = await supabase
        .from('store_tab_products')
        .select('*')
        .eq('seller_store_id', storeData.id);

      console.log('Tab Products 데이터:', tabProducts);

      if (!tabError && tabProducts) {
        // 각 탭별로 상품 데이터 구성
        const newSubTabProducts: { [key: string]: Product[] } = {
          '탭 1': [],
          '탭 2': [],
          '탭 3': [],
          '탭 4': []
        };

        // seller_store에서 이미 로드한 서브탭 이름 사용
        const currentSubTabNames = {
          '탭 1': storeData.sub_tab_1 || '탭 1',
          '탭 2': storeData.sub_tab_2 || '탭 2',
          '탭 3': storeData.sub_tab_3 || '탭 3',
          '탭 4': storeData.sub_tab_4 || '탭 4'
        };

        console.log('현재 서브탭 이름 설정:', currentSubTabNames);

        // 탭 인덱스와 키 매핑
        const tabIndexToKeyMap = {
          1: '탭 1',
          2: '탭 2',
          3: '탭 3',
          4: '탭 4'
        };
        
        for (const tabProduct of tabProducts) {
          console.log('처리 중인 탭 상품:', tabProduct);
          
          // 상품 정보 가져오기
          if (tabProduct.product_id) {
            const { data: productData, error: productError } = await supabase
              .from('products')
              .select('*')
              .eq('product_id', tabProduct.product_id)
              .single();

            if (!productError && productData) {
              console.log('상품 데이터:', productData);
              
              // tab_index로 탭 키 찾기
              const tabKey = tabIndexToKeyMap[tabProduct.tab_index as keyof typeof tabIndexToKeyMap];

              console.log('탭 인덱스 매칭:', {
                tab_index: tabProduct.tab_index,
                matched_key: tabKey
              });

              if (tabKey) {
                newSubTabProducts[tabKey].push({
                  product_id: productData.product_id,
                  product_name: productData.product_name,
                  price: productData.price,
                  main_images: productData.main_images || [],
                  keywords: productData.keywords || [],
                  description: productData.description,
                  store_name: productData.store_name
                });
                console.log(`${tabKey} 탭에 상품 추가됨:`, productData.product_name);
              } else {
                console.log('탭 인덱스 매칭 실패:', tabProduct.tab_index);
              }
            } else {
              console.error('상품 데이터 조회 실패:', productError);
            }
          }
        }

        console.log('로드된 SubTab Products:', newSubTabProducts);
        setSubTabProducts(newSubTabProducts);
      }

      console.log('데이터 로드 완료');

    } catch (error) {
      console.error('저장된 데이터 로드 중 오류:', error);
    }
  };

  // 페이지 로드 시 저장된 데이터 불러오기
  useEffect(() => {
    if (user) {
      loadSavedData();
    }
  }, [user]);

  // 검색된 상품 필터링
  console.log('🔍 상품 필터링:', {
    totalProducts: products.length,
    searchTerm: productSearchTerm,
    products: products.map(p => ({ id: p.product_id, name: p.product_name }))
  });

  const filteredProducts = products.filter(product =>
    product.product_name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    product.store_name.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  console.log('🔍 필터링 결과:', {
    filteredCount: filteredProducts.length,
    filteredProducts: filteredProducts.map(p => ({ id: p.product_id, name: p.product_name }))
  });

  // 미리보기용 이미지 경로 (새로 업로드된 파일 우선, 없으면 저장된 URL 사용)
  const previewImages = useMemo(() => {
    return imageFiles.main.length > 0
      ? imageFiles.main.map(f => URL.createObjectURL(f))
      : previewData.images.main;
  }, [imageFiles.main, previewData.images.main]);

  const previewSub1Images = useMemo(() => {
    return imageFiles.sub1.length > 0
      ? imageFiles.sub1.map(f => URL.createObjectURL(f))
      : previewData.images.sub1;
  }, [imageFiles.sub1, previewData.images.sub1]);

  const previewSub2Images = useMemo(() => {
    return imageFiles.sub2.length > 0
      ? imageFiles.sub2.map(f => URL.createObjectURL(f))
      : previewData.images.sub2;
  }, [imageFiles.sub2, previewData.images.sub2]);

  // 메인 이미지 자동 슬라이드
  useEffect(() => {
    if (previewImages.length <= 1) return;

    const interval = setInterval(() => {
      setMainImageIndex(prev => (prev + 1) % previewImages.length);
    }, 3000); // 3초마다 변경

    return () => clearInterval(interval);
  }, [previewImages.length]);

  // 서브1 이미지 자동 슬라이드
  useEffect(() => {
    if (previewSub1Images.length <= 1) return;

    const interval = setInterval(() => {
      setSub1ImageIndex(prev => (prev + 1) % previewSub1Images.length);
    }, 3500); // 3.5초마다 변경

    return () => clearInterval(interval);
  }, [previewSub1Images.length]);

  // 서브2 이미지 자동 슬라이드
  useEffect(() => {
    if (previewSub2Images.length <= 1) return;

    const interval = setInterval(() => {
      setSub2ImageIndex(prev => (prev + 1) % previewSub2Images.length);
    }, 4000); // 4초마다 변경

    return () => clearInterval(interval);
  }, [previewSub2Images.length]);

  // 저장하기 핸들러
  const handleSave = async () => {
    // 이미 저장 중이면 중복 실행 방지
    if (isSaving) {
      console.log('⚠️ 이미 저장 중입니다.');
      return;
    }

    setIsSaving(true); // 저장 시작

    try {
      // useAuth에서 사용자 정보 가져오기
      if (!user) {
        alert('로그인이 필요합니다.');
        setIsSaving(false);
        return;
      }

      // seller_profile에서 seller_id 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        alert('Seller 정보를 찾을 수 없습니다.');
        return;
      }

      // 이미지 업로드 함수 - R2로 업로드 (새 파일만)
      const uploadImages = async (files: File[], zone: number, existingCount: number) => {
        const uploadedUrls: string[] = [];

        for (let index = 0; index < files.length; index++) {
          const file = files[index];
          console.log(`🚀 R2로 샵 이미지 업로드 시작: ${file.name} (Zone: ${zone}, Index: ${existingCount + index})`);

          try {
            const uploadUrl = await uploadShopImage(file, sellerProfile.id.toString(), zone, existingCount + index);

            if (uploadUrl && typeof uploadUrl === 'string') {
              console.log(`✅ R2 샵 이미지 업로드 성공:`, uploadUrl);
              uploadedUrls.push(uploadUrl);
            } else {
              console.error(`❌ R2 샵 이미지 업로드 실패:`, uploadUrl);
              throw new Error(`이미지 업로드에 실패했습니다.`);
            }
          } catch (error) {
            console.error('❌ R2 샵 이미지 업로드 중 오류:', error);
            throw new Error('이미지 업로드에 실패했습니다.');
          }
        }

        return uploadedUrls;
      };

      // 새로 업로드된 이미지만 R2에 업로드 (기존 URL 개수를 고려한 인덱스)
      const newLogoUrls = imageFiles.logo.length > 0 ? await uploadImages(imageFiles.logo, 1, existingImageUrls.logo.length) : [];
      const newMainUrls = imageFiles.main.length > 0 ? await uploadImages(imageFiles.main, 2, existingImageUrls.main.length) : [];
      const newSub1Urls = imageFiles.sub1.length > 0 ? await uploadImages(imageFiles.sub1, 3, existingImageUrls.sub1.length) : [];
      const newSub2Urls = imageFiles.sub2.length > 0 ? await uploadImages(imageFiles.sub2, 4, existingImageUrls.sub2.length) : [];

      // 기존 URL + 새 URL 병합
      const finalLogoUrls = [...existingImageUrls.logo, ...newLogoUrls.filter(Boolean)];
      const finalMainUrls = [...existingImageUrls.main, ...newMainUrls.filter(Boolean)];
      const finalSub1Urls = [...existingImageUrls.sub1, ...newSub1Urls.filter(Boolean)];
      const finalSub2Urls = [...existingImageUrls.sub2, ...newSub2Urls.filter(Boolean)];

      console.log('최종 로고 이미지 URL:', finalLogoUrls);
      console.log('최종 메인 이미지 URL:', finalMainUrls);
      console.log('최종 서브1 이미지 URL:', finalSub1Urls);
      console.log('최종 서브2 이미지 URL:', finalSub2Urls);

      // 삭제된 이미지 찾기 (원본에는 있었지만 최종 URL에는 없는 것)
      const deletedLogoUrls = originalImageUrls.logo.filter(url => !finalLogoUrls.includes(url));
      const deletedMainUrls = originalImageUrls.main.filter(url => !finalMainUrls.includes(url));
      const deletedSub1Urls = originalImageUrls.sub1.filter(url => !finalSub1Urls.includes(url));
      const deletedSub2Urls = originalImageUrls.sub2.filter(url => !finalSub2Urls.includes(url));

      console.log('삭제될 로고 이미지:', deletedLogoUrls);
      console.log('삭제될 메인 이미지:', deletedMainUrls);
      console.log('삭제될 서브1 이미지:', deletedSub1Urls);
      console.log('삭제될 서브2 이미지:', deletedSub2Urls);

      // R2에서 삭제된 이미지 제거
      const allDeletedUrls = [...deletedLogoUrls, ...deletedMainUrls, ...deletedSub1Urls, ...deletedSub2Urls];
      if (allDeletedUrls.length > 0) {
        console.log(`🗑️ ${allDeletedUrls.length}개 이미지 R2에서 삭제 시작`);
        await deleteMultipleFromR2(allDeletedUrls);
      }

      // 기존 seller_store 데이터 확인
      const { data: existingStore, error: checkError } = await supabase
        .from('seller_store')
        .select('*')
        .eq('seller_id', sellerProfile.id)
        .single();

      let storeData;
      let storeError;

      if (existingStore) {
        // 기존 데이터가 있으면 업데이트
        console.log('🔄 기존 데이터 업데이트');
        const { data: updatedStore, error: updateError } = await supabase
          .from('seller_store')
          .update({
            store_name: previewData.brandName,
            store_description: previewData.brandDescription,
            promotion_text: [previewData.promotionText], // 배열로 저장
            sub_tab_title: previewData.weeklyBestTitle,
            logo_image: finalLogoUrls.length > 0 ? finalLogoUrls[0] : null, // 로고 이미지 (1장만)
            main_image: finalMainUrls.length > 0 ? finalMainUrls : null, // 배열 직접 저장
            sub_image_1: finalSub1Urls.length > 0 ? finalSub1Urls : null, // 배열 직접 저장
            sub_image_2: finalSub2Urls.length > 0 ? finalSub2Urls : null, // 배열 직접 저장
            sub_tab_1: previewData.subTabNames['탭 1'],
            sub_tab_2: previewData.subTabNames['탭 2'],
            sub_tab_3: previewData.subTabNames['탭 3'],
            sub_tab_4: previewData.subTabNames['탭 4'],
            search_keywords: keywords // 검색 키워드 추가
          })
          .eq('seller_id', sellerProfile.id)
          .select()
          .single();
        
        storeData = updatedStore;
        storeError = updateError;
      } else {
        // 기존 데이터가 없으면 새로 생성
        console.log('🆕 새 데이터 생성');
        const { data: newStore, error: insertError } = await supabase
          .from('seller_store')
          .insert({
            seller_id: sellerProfile.id,
            store_name: previewData.brandName,
            store_description: previewData.brandDescription,
            promotion_text: [previewData.promotionText], // 배열로 저장
            sub_tab_title: previewData.weeklyBestTitle,
            logo_image: finalLogoUrls.length > 0 ? finalLogoUrls[0] : null, // 로고 이미지 (1장만)
            main_image: finalMainUrls.length > 0 ? finalMainUrls : null, // 배열 직접 저장
            sub_image_1: finalSub1Urls.length > 0 ? finalSub1Urls : null, // 배열 직접 저장
            sub_image_2: finalSub2Urls.length > 0 ? finalSub2Urls : null, // 배열 직접 저장
            sub_tab_1: previewData.subTabNames['탭 1'],
            sub_tab_2: previewData.subTabNames['탭 2'],
            sub_tab_3: previewData.subTabNames['탭 3'],
            sub_tab_4: previewData.subTabNames['탭 4'],
            search_keywords: keywords // 검색 키워드 추가
          })
          .select()
          .single();
        
        storeData = newStore;
        storeError = insertError;
      }

      if (storeError) {
        console.error('Store 데이터 저장 실패:', storeError);
        alert('저장에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      // seller_profile의 store_name도 동기화
      console.log('🔄 seller_profile의 store_name 동기화');
      const { error: profileUpdateError } = await supabase
        .from('seller_profile')
        .update({ store_name: previewData.brandName })
        .eq('id', sellerProfile.id);

      if (profileUpdateError) {
        console.error('seller_profile 업데이트 실패:', profileUpdateError);
        // 에러가 나도 seller_store는 저장되었으므로 계속 진행
      } else {
        console.log('✅ seller_profile의 store_name 동기화 완료');
      }

      // 기존 store_tab_products 데이터 삭제
      console.log('🗑️ 기존 store_tab_products 데이터 삭제');
      const { error: deleteError } = await supabase
        .from('store_tab_products')
        .delete()
        .eq('seller_store_id', storeData.id);

      if (deleteError) {
        console.error('기존 store_tab_products 삭제 실패:', deleteError);
      } else {
        console.log('✅ 기존 store_tab_products 삭제 완료');
      }

      // store_tab_products 테이블에 서브탭별 상품 저장
      console.log('💾 새로운 store_tab_products 데이터 저장');
      
      // 탭 키와 인덱스 매핑
      const tabIndexMap = {
        '탭 1': 1,
        '탭 2': 2, 
        '탭 3': 3,
        '탭 4': 4
      };
      
      for (const [tabKey, products] of Object.entries(subTabProducts)) {
        const tabIndex = tabIndexMap[tabKey as keyof typeof tabIndexMap];
        
        // 상품이 있는 경우에만 저장
        if (products.length > 0) {
          for (const product of products) {
            const { error: productError } = await supabase
              .from('store_tab_products')
              .insert({
                seller_store_id: storeData.id,
                tab_index: tabIndex,
                product_id: product.product_id
              });

            if (productError) {
              console.error('상품 데이터 저장 실패:', productError);
            } else {
              console.log(`✅ 탭 ${tabIndex}에 상품 추가:`, product.product_name);
            }
          }
        } else {
          // 상품이 없어도 탭 자체는 저장 (빈 탭)
          const { error: tabError } = await supabase
            .from('store_tab_products')
            .insert({
              seller_store_id: storeData.id,
              tab_index: tabIndex,
              product_id: null // 상품이 없는 경우
            });

          if (tabError) {
            console.error('탭 데이터 저장 실패:', tabError);
          } else {
            console.log(`✅ 탭 ${tabIndex} 저장 (빈 탭)`);
          }
        }
      }

      alert('저장이 완료되었습니다!');
      
      // 성공 후 dashboard로 이동
      setTimeout(() => {
        navigate('/seller');
      }, 1000);
      
    } catch (error) {
      console.error('저장 중 오류:', error);
      alert('저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false); // 저장 완료
    }
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* 저장 중일 때 화면 클릭 비활성화 오버레이 */}
      {isSaving && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="h6">{t("seller.shop.saving")}</Typography>
            <Typography variant="body2" color="text.secondary">
              잠시만 기다려주세요
            </Typography>
          </Box>
        </Box>
      )}
      
      <Typography variant="h4" fontWeight={700} mb={3} textAlign="center">
        Shop 페이지 편집
      </Typography>
      
      <Grid container spacing={1}>
        {/* 편집 필드 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, bgcolor: isDarkMode ? '#2d2d2d' : '#fff', borderRadius: 4, boxShadow: 2 }}>
            <Typography variant="h6" fontWeight={700} mb={3} sx={{ color: isDarkMode ? '#fff' : '#000' }}>
              편집 필드
            </Typography>
            
            <Stack spacing={3}>
              {/* 이미지 업로드 */}
              <Box>
                <Typography fontWeight={700} mb={2} sx={{ color: isDarkMode ? '#fff' : '#000' }}>이미지 업로드</Typography>
                
                {/* 로고 이미지 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" mb={1} sx={{ color: isDarkMode ? '#fff' : '#000' }}>로고 이미지 (1장)_ 100px</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() => handleImageChange('logo')}
                    size="small"
                    sx={{ 
                      mb: 1,
                      borderColor: isDarkMode ? '#fff' : '#000',
                      color: isDarkMode ? '#fff' : '#000',
                      '&:hover': {
                        borderColor: isDarkMode ? '#ccc' : '#333',
                        color: isDarkMode ? '#ccc' : '#333',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    업로드
                  </Button>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {/* 기존 URL 로고 이미지 먼저 표시 */}
                    {existingImageUrls.logo.map((url, idx) => (
                      <Box
                        key={`existing-logo-${idx}`}
                        sx={{
                          position: 'relative',
                          borderRadius: 2
                        }}
                      >
                        <Avatar variant="rounded" src={url} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('logo', idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 새로 업로드된 로고 이미지 표시 */}
                    {imageFiles.logo.map((file, idx) => (
                      <Box
                        key={`new-logo-${idx}`}
                        sx={{
                          position: 'relative',
                          borderRadius: 2
                        }}
                      >
                        <Avatar variant="rounded" src={URL.createObjectURL(file)} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('logo', existingImageUrls.logo.length + idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 업로드 버튼 (로고가 없을 때만 표시) */}
                    {(existingImageUrls.logo.length + imageFiles.logo.length) === 0 && (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          border: '2px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#1976d2',
                            bgcolor: '#f5f5f5'
                          }
                        }}
                        onClick={() => handleImageChange('logo')}
                      >
                        <AddPhotoAlternateIcon sx={{ color: '#999', fontSize: 24 }} />
                      </Box>
                    )}
                  </Stack>
                </Box>
                
                {/* 메인 이미지 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" mb={1} sx={{ color: isDarkMode ? '#fff' : '#000' }}>메인 이미지 (최대 5장)_ 250px</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() => handleImageChange('main')}
                    size="small"
                    sx={{ 
                      mb: 1,
                      borderColor: isDarkMode ? '#fff' : '#000',
                      color: isDarkMode ? '#fff' : '#000',
                      '&:hover': {
                        borderColor: isDarkMode ? '#ccc' : '#333',
                        color: isDarkMode ? '#ccc' : '#333',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    업로드
                  </Button>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {/* 기존 URL 메인 이미지 먼저 표시 */}
                    {existingImageUrls.main.map((url, idx) => (
                      <Box
                        key={`existing-main-${idx}`}
                        sx={{
                          position: 'relative',
                          borderRadius: 2
                        }}
                      >
                        <Avatar variant="rounded" src={url} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('main', idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 새로 업로드된 메인 이미지 표시 */}
                    {imageFiles.main.map((file, idx) => (
                      <Box
                        key={`new-main-${idx}`}
                        sx={{
                          position: 'relative',
                          cursor: 'grab',
                          opacity: draggingIdx === idx ? 0.5 : 1,
                          border: dragOverIdx === idx ? '2px solid #1976d2' : 'none',
                          borderRadius: 2
                        }}
                        draggable
                        onDragStart={() => setDraggingIdx(idx)}
                        onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggingIdx !== null && draggingIdx !== idx) {
                            const newFiles = [...imageFiles.main];
                            const [removed] = newFiles.splice(draggingIdx, 1);
                            newFiles.splice(idx, 0, removed);
                            setImageFiles(prev => ({ ...prev, main: newFiles }));
                          }
                          setDraggingIdx(null);
                          setDragOverIdx(null);
                        }}
                        onDragEnd={() => { setDraggingIdx(null); setDragOverIdx(null); }}
                      >
                        <Avatar variant="rounded" src={URL.createObjectURL(file)} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('main', existingImageUrls.main.length + idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 업로드 버튼 (이미지가 5개 미만일 때만 표시) */}
                    {(existingImageUrls.main.length + imageFiles.main.length) < 5 && (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          border: '2px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#1976d2',
                            bgcolor: '#f5f5f5'
                          }
                        }}
                        onClick={() => handleImageChange('main')}
                      >
                        <AddPhotoAlternateIcon sx={{ color: '#999', fontSize: 24 }} />
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* 서브 이미지 1 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" mb={1} sx={{ color: isDarkMode ? '#fff' : '#000' }}>서브 이미지 1 (최대 5장)_160px</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() => handleImageChange('sub1')}
                    size="small"
                    sx={{ 
                      mb: 1,
                      borderColor: isDarkMode ? '#fff' : '#000',
                      color: isDarkMode ? '#fff' : '#000',
                      '&:hover': {
                        borderColor: isDarkMode ? '#ccc' : '#333',
                        color: isDarkMode ? '#ccc' : '#333',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    업로드
                  </Button>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {/* 기존 URL 서브1 이미지 먼저 표시 */}
                    {existingImageUrls.sub1.map((url, idx) => (
                      <Box
                        key={`existing-sub1-${idx}`}
                        sx={{
                          position: 'relative',
                          borderRadius: 2
                        }}
                      >
                        <Avatar variant="rounded" src={url} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('sub1', idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 새로 업로드된 서브1 이미지 표시 */}
                    {imageFiles.sub1.map((file, idx) => (
                      <Box
                        key={`new-sub1-${idx}`}
                        sx={{
                          position: 'relative',
                          cursor: 'grab',
                          opacity: detailDraggingIdx === idx ? 0.5 : 1,
                          border: detailDragOverIdx === idx ? '2px solid #1976d2' : 'none',
                          borderRadius: 2
                        }}
                        draggable
                        onDragStart={() => setDetailDraggingIdx(idx)}
                        onDragOver={(e) => { e.preventDefault(); setDetailDragOverIdx(idx); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (detailDraggingIdx !== null && detailDraggingIdx !== idx) {
                            const newFiles = [...imageFiles.sub1];
                            const [removed] = newFiles.splice(detailDraggingIdx, 1);
                            newFiles.splice(idx, 0, removed);
                            setImageFiles(prev => ({ ...prev, sub1: newFiles }));
                          }
                          setDetailDraggingIdx(null);
                          setDetailDragOverIdx(null);
                        }}
                        onDragEnd={() => { setDetailDraggingIdx(null); setDetailDragOverIdx(null); }}
                      >
                        <Avatar variant="rounded" src={URL.createObjectURL(file)} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('sub1', existingImageUrls.sub1.length + idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 업로드 버튼 (이미지가 5개 미만일 때만 표시) */}
                    {(existingImageUrls.sub1.length + imageFiles.sub1.length) < 5 && (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          border: '2px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#1976d2',
                            bgcolor: '#f5f5f5'
                          }
                        }}
                        onClick={() => handleImageChange('sub1')}
                      >
                        <AddPhotoAlternateIcon sx={{ color: '#999', fontSize: 24 }} />
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* 서브 이미지 2 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" mb={1} sx={{ color: isDarkMode ? '#fff' : '#000' }}>서브 이미지 2 (최대 5장_160px)</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() => handleImageChange('sub2')}
                    size="small"
                    sx={{ 
                      mb: 1,
                      borderColor: isDarkMode ? '#fff' : '#000',
                      color: isDarkMode ? '#fff' : '#000',
                      '&:hover': {
                        borderColor: isDarkMode ? '#ccc' : '#333',
                        color: isDarkMode ? '#ccc' : '#333',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    업로드
                  </Button>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {/* 기존 URL 서브2 이미지 먼저 표시 */}
                    {existingImageUrls.sub2.map((url, idx) => (
                      <Box
                        key={`existing-sub2-${idx}`}
                        sx={{
                          position: 'relative',
                          borderRadius: 2
                        }}
                      >
                        <Avatar variant="rounded" src={url} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('sub2', idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 새로 업로드된 서브2 이미지 표시 */}
                    {imageFiles.sub2.map((file, idx) => (
                      <Box
                        key={`new-sub2-${idx}`}
                        sx={{
                          position: 'relative',
                          cursor: 'grab',
                          opacity: detailDraggingIdx === idx ? 0.5 : 1,
                          border: detailDragOverIdx === idx ? '2px solid #1976d2' : 'none',
                          borderRadius: 2
                        }}
                        draggable
                        onDragStart={() => setDetailDraggingIdx(idx)}
                        onDragOver={(e) => { e.preventDefault(); setDetailDragOverIdx(idx); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (detailDraggingIdx !== null && detailDraggingIdx !== idx) {
                            const newFiles = [...imageFiles.sub2];
                            const [removed] = newFiles.splice(detailDraggingIdx, 1);
                            newFiles.splice(idx, 0, removed);
                            setImageFiles(prev => ({ ...prev, sub2: newFiles }));
                          }
                          setDetailDraggingIdx(null);
                          setDetailDragOverIdx(null);
                        }}
                        onDragEnd={() => { setDetailDraggingIdx(null); setDetailDragOverIdx(null); }}
                      >
                        <Avatar variant="rounded" src={URL.createObjectURL(file)} sx={{ width: 80, height: 80 }} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }}
                          onClick={() => handleRemoveImage('sub2', existingImageUrls.sub2.length + idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {/* 업로드 버튼 (이미지가 5개 미만일 때만 표시) */}
                    {(existingImageUrls.sub2.length + imageFiles.sub2.length) < 5 && (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          border: '2px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#1976d2',
                            bgcolor: '#f5f5f5'
                          }
                        }}
                        onClick={() => handleImageChange('sub2')}
                      >
                        <AddPhotoAlternateIcon sx={{ color: '#999', fontSize: 24 }} />
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>

              {/* 브랜드명 (페이지 제목과 동일) */}
              <TextField
                fullWidth
                label="브랜드명"
                value={previewData.brandName}
                onChange={(e) => {
                  handleTextChange('brandName', e.target.value);
                  handleTextChange('pageTitle', e.target.value);
                }}
                placeholder="브랜드명을 입력하세요"
                sx={{
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#fff' : '#666',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#fff' : '#000',
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    color: isDarkMode ? '#fff' : '#000',
                    '& fieldset': {
                      borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#fff' : '#000',
                    },
                  },
                }}
              />

              {/* 브랜드 설명 */}
              <TextField
                fullWidth
                label="브랜드 설명"
                value={previewData.brandDescription}
                onChange={(e) => handleTextChange('brandDescription', e.target.value)}
                placeholder="브랜드 설명을 입력하세요"
                multiline
                rows={2}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#fff' : '#666',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#fff' : '#000',
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    color: isDarkMode ? '#fff' : '#000',
                    '& fieldset': {
                      borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#fff' : '#000',
                    },
                  },
                }}
              />

              {/* 프로모션 텍스트 */}
              <TextField
                fullWidth
                label="프로모션 텍스트"
                value={previewData.promotionText}
                onChange={(e) => handleTextChange('promotionText', e.target.value)}
                placeholder="프로모션 텍스트를 입력하세요"
                multiline
                rows={2}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#fff' : '#666',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#fff' : '#000',
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    color: isDarkMode ? '#fff' : '#000',
                    '& fieldset': {
                      borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#fff' : '#000',
                    },
                  },
                }}
              />

              {/* 서브 탭 제목 */}
              <TextField
                fullWidth
                label="서브 탭 제목"
                value={previewData.weeklyBestTitle}
                onChange={(e) => handleTextChange('weeklyBestTitle', e.target.value)}
                placeholder="서브 탭 제목을 입력하세요"
                sx={{
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#fff' : '#666',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#fff' : '#000',
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    color: isDarkMode ? '#fff' : '#000',
                    '& fieldset': {
                      borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#fff' : '#000',
                    },
                  },
                }}
              />

              {/* 서브탭 이름 설정 */}
              <Box>
                <Typography fontWeight={700} mb={2} sx={{ color: isDarkMode ? '#fff' : '#000' }}>서브탭 이름 설정</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="첫 번째 탭"
                      value={previewData.subTabNames['탭 1']}
                      onChange={(e) => setPreviewData(prev => ({
                        ...prev,
                        subTabNames: { ...prev.subTabNames, '탭 1': e.target.value }
                      }))}
                      placeholder="탭 이름을 입력하세요"
                      size="small"
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#fff' : '#666',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#fff' : '#000',
                          },
                        },
                        '& .MuiOutlinedInput-root': {
                          color: isDarkMode ? '#fff' : '#000',
                          '& fieldset': {
                            borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#fff' : '#000',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="두 번째 탭"
                      value={previewData.subTabNames['탭 2']}
                      onChange={(e) => setPreviewData(prev => ({
                        ...prev,
                        subTabNames: { ...prev.subTabNames, '탭 2': e.target.value }
                      }))}
                      placeholder="탭 이름을 입력하세요"
                      size="small"
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#fff' : '#666',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#fff' : '#000',
                          },
                        },
                        '& .MuiOutlinedInput-root': {
                          color: isDarkMode ? '#fff' : '#000',
                          '& fieldset': {
                            borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#fff' : '#000',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="세 번째 탭"
                      value={previewData.subTabNames['탭 3']}
                      onChange={(e) => setPreviewData(prev => ({
                        ...prev,
                        subTabNames: { ...prev.subTabNames, '탭 3': e.target.value }
                      }))}
                      placeholder="탭 이름을 입력하세요"
                      size="small"
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#fff' : '#666',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#fff' : '#000',
                          },
                        },
                        '& .MuiOutlinedInput-root': {
                          color: isDarkMode ? '#fff' : '#000',
                          '& fieldset': {
                            borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#fff' : '#000',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="네 번째 탭"
                      value={previewData.subTabNames['탭 4']}
                      onChange={(e) => setPreviewData(prev => ({
                        ...prev,
                        subTabNames: { ...prev.subTabNames, '탭 4': e.target.value }
                      }))}
                      placeholder="탭 이름을 입력하세요"
                      size="small"
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#fff' : '#666',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#fff' : '#000',
                          },
                        },
                        '& .MuiOutlinedInput-root': {
                          color: isDarkMode ? '#fff' : '#000',
                          '& fieldset': {
                            borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? '#808080' : '#c0c0c0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#fff' : '#000',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* 크리에이터 검색 키워드 */}
              <Box>
                <Typography fontWeight={700} mb={1} sx={{ color: isDarkMode ? '#fff' : '#000' }}>
                  크리에이터 검색 키워드
                </Typography>
                <Typography variant="body2" mb={2} sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
                  크리에이터 발견 페이지에서 내 스토어를 검색할 때 사용될 키워드를 설정하세요.
                </Typography>
                <KeywordSelector
                  keywords={keywords}
                  setKeywords={setKeywords}
                />
              </Box>

                              {/* 서브 탭별 상품 관리 */}
              <Box>
                <Typography fontWeight={700} mb={2} sx={{ color: isDarkMode ? '#fff' : '#000' }}>서브탭별 상품 관리</Typography>
                {['탭 1', '탭 2', '탭 3', '탭 4'].map((tab) => (
                  <Box key={tab} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: isDarkMode ? '#fff' : '#000' }}>
                        {previewData.subTabNames[tab as keyof typeof previewData.subTabNames]}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddCircleIcon />}
                        onClick={() => {
                          handleSubTabChange(tab);
                          fetchProducts();
                          setProductDialogOpen(true);
                        }}
                        sx={{
                          borderColor: isDarkMode ? '#fff' : '#000',
                          color: isDarkMode ? '#fff' : '#000',
                          '&:hover': {
                            borderColor: isDarkMode ? '#ccc' : '#333',
                            color: isDarkMode ? '#ccc' : '#333',
                            bgcolor: 'transparent'
                          }
                        }}
                      >
                        상품 추가
                      </Button>
                    </Box>
                    
                    {/* 상품 리스트 (row 형태) */}
                    <Stack spacing={1}>
                      {subTabProducts[tab]?.map((product, idx) => (
                        <Box key={product.product_id} sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: isDarkMode ? '#404040' : '#f8f9fa', 
                          borderRadius: 2, 
                          p: 1.5, 
                          position: 'relative',
                          border: isDarkMode ? '1px solid #606060' : '1px solid #e9ecef'
                        }}>
                          <Box sx={{
                            width: 60,
                            height: 60,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0
                          }}>
                            {product.main_images?.[0] ? (
                              <img 
                                src={product.main_images[0]} 
                                alt={product.product_name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: 12, color: '#999' }}>🏔️</Typography>
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, lineHeight: 1.2, color: isDarkMode ? '#fff' : '#000' }}>
                              {product.product_name}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: isDarkMode ? '#ccc' : '#666', mb: 0.5 }}>
                              {product.price.toLocaleString()}원
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: isDarkMode ? '#aaa' : '#999' }}>
                              {product.store_name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{ 
                              bgcolor: '#fff',
                              width: 24,
                              height: 24,
                              boxShadow: 1,
                              flexShrink: 0
                            }}
                            onClick={() => handleRemoveProduct(tab, product.product_id)}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* 모바일 미리보기 */}
        <Grid item>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Paper sx={{ p: 0, bgcolor: '#fff', borderRadius: 4, boxShadow: 2, overflow: 'hidden', width: 400 }}>
            {/* 모바일 화면 */}
            <Box sx={{
              width: 400,
              bgcolor: '#fff',
              overflow: 'hidden',
              position: 'relative',
              mx: 'auto'
            }}>
              {/* 네비게이션 바 */}
              <Box sx={{
                height: 44,
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                borderBottom: '1px solid #eee'
              }}>
                <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 700 }}>‹</Typography>
                </Box>
                
                {/* 로고 이미지 또는 페이지 제목 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {previewData.images.logo.length > 0 ? (
                    <img 
                      src={previewData.images.logo[0]} 
                      alt="로고"
                      style={{ width: 24, height: 24, objectFit: 'contain' }}
                    />
                  ) : null}
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {previewData.pageTitle}
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={handleLikeToggle}
                >
                  {isLiked ? (
                    <FavoriteIcon sx={{ 
                      fontSize: 20,
                      color: '#FF777A'
                    }} />
                  ) : (
                    <FavoriteBorderIcon sx={{ 
                      fontSize: 20,
                      color: '#666'
                    }} />
                  )}
                </Box>
              </Box>

              {/* 메인 컨텐츠 */}
              <Box sx={{ p: 2, overflowY: 'auto', bgcolor: '#fff' }}>
                {/* 메인 이미지 */}
                <Box sx={{
                  width: '100%',
                  height: 250,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {previewImages.length > 0 ? (
                    <img
                      src={previewImages[mainImageIndex]}
                      alt="메인 이미지"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'opacity 0.5s ease-in-out'
                      }}
                    />
                  ) : (
                    <Box sx={{
                      width: 32,
                      height: 32,
                      bgcolor: '#fff',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ fontSize: 20, color: '#999' }}>🏔️</Typography>
                    </Box>
                  )}
                  {/* 이미지 인디케이터 */}
                  {previewImages.length > 1 && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: 0.5
                    }}>
                      {previewImages.map((_, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: idx === mainImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* 서브 이미지들 */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <Box sx={{
                    flex: 1,
                    height: 160,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {previewSub1Images.length > 0 ? (
                      <>
                        <img
                          src={previewSub1Images[sub1ImageIndex]}
                          alt="서브 이미지 1"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'opacity 0.5s ease-in-out'
                          }}
                        />
                        {/* 이미지 인디케이터 */}
                        {previewSub1Images.length > 1 && (
                          <Box sx={{
                            position: 'absolute',
                            bottom: 6,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: 0.4
                          }}>
                            {previewSub1Images.map((_, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: '50%',
                                  bgcolor: idx === sub1ImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{
                        width: 24,
                        height: 24,
                        bgcolor: '#fff',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: 16, color: '#999' }}>🏔️</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{
                    flex: 1,
                    height: 160,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {previewSub2Images.length > 0 ? (
                      <>
                        <img
                          src={previewSub2Images[sub2ImageIndex]}
                          alt="서브 이미지 2"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'opacity 0.5s ease-in-out'
                          }}
                        />
                        {/* 이미지 인디케이터 */}
                        {previewSub2Images.length > 1 && (
                          <Box sx={{
                            position: 'absolute',
                            bottom: 6,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: 0.4
                          }}>
                            {previewSub2Images.map((_, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: '50%',
                                  bgcolor: idx === sub2ImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{
                        width: 24,
                        height: 24,
                        bgcolor: '#fff',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: 16, color: '#999' }}>🏔️</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 브랜드 정보 */}
                <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1 }}>
                  {previewData.brandName}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
                  {previewData.brandDescription}
                </Typography>

                {/* 프로모션 박스 */}
                <Box sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  p: 2,
                  mb: 3
                }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                    {previewData.promotionText}
                  </Typography>
                </Box>

                {/* 탭 네비게이션 */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Box sx={{
                    flex: 1,
                    textAlign: 'center',
                    pb: 1,
                    borderBottom: previewData.selectedTab === 'product' ? '2px solid #000' : 'none'
                  }}>
                    <Typography sx={{ 
                      fontSize: 13, 
                      fontWeight: previewData.selectedTab === 'product' ? 700 : 400,
                      color: previewData.selectedTab === 'product' ? '#000' : '#666'
                    }}>
                      product
                    </Typography>
                  </Box>
                  <Box sx={{
                    flex: 1,
                    textAlign: 'center',
                    pb: 1,
                    borderBottom: previewData.selectedTab === 'contents' ? '2px solid #000' : 'none'
                  }}>
                    <Typography sx={{ 
                      fontSize: 13, 
                      fontWeight: previewData.selectedTab === 'contents' ? 700 : 400,
                      color: previewData.selectedTab === 'contents' ? '#000' : '#666'
                    }}>
                      contents
                    </Typography>
                  </Box>
                </Box>

                                {/* Weekly Best 섹션 */}
                <Box sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  p: 2,
                  mt: 3,
                  width: '100%'
                }}>
                  {/* Weekly Best 제목 */}
                  <Typography sx={{ 
                    fontSize: 16, 
                    fontWeight: 700, 
                    mb: 2,
                    textAlign: 'center',
                    color: '#333'
                  }}>
                    {previewData.weeklyBestTitle}
                  </Typography>
 
                  {/* 서브 탭들 */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 4,
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    {['탭 1', '탭 2', '탭 3', '탭 4'].map((tab) => (
                      <Typography
                        key={tab}
                        sx={{
                          fontSize: 13,
                          fontWeight: previewData.selectedSubTab === tab ? 700 : 400,
                          color: previewData.selectedSubTab === tab ? '#000' : '#666',
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }}
                        onClick={() => handleSubTabChange(tab)}
                      >
                        {previewData.subTabNames[tab as keyof typeof previewData.subTabNames]}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* 상품 그리드 (흰색 배경) */}
                <Box sx={{ 
                  bgcolor: '#fff', 
                  borderRadius: 2, 
                  p: 2, 
                  mt: 1,
                  width: '100%'
                }}>
                  <Grid container spacing={1} columns={3}>
                    {subTabProducts[previewData.selectedSubTab]?.map((product, idx) => (
                      <Grid item xs={1} key={product.product_id}>
                        <Box sx={{ 
                          bgcolor: '#fff', 
                          borderRadius: 2, 
                          p: 1, 
                          position: 'relative',
                          border: '1px solid #e9ecef',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Box sx={{
                            width: '100%',
                            height: 120,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                            overflow: 'hidden'
                          }}>
                            {product.main_images?.[0] ? (
                              <img 
                                src={product.main_images[0]} 
                                alt={product.product_name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: 16, color: '#999' }}>🏔️</Typography>
                            )}
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Typography sx={{ 
                              fontSize: 11, 
                              fontWeight: 600, 
                              mb: 0.5, 
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {product.product_name}
                            </Typography>
                            <Box>
                              <Typography sx={{ fontSize: 10, color: '#666', mb: 0.5, fontWeight: 600 }}>
                                ₩{product.price.toLocaleString()}
                              </Typography>
                              <Typography sx={{ fontSize: 9, color: '#999' }}>
                                {product.store_name}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Paper>
          
          {/* 저장하기 버튼 */}
          <Button
            variant="contained"
            size="large"
            disabled={isSaving}
            sx={{
              mt: 3,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontSize: 16,
              fontWeight: 600,
              bgcolor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': {
                bgcolor: isDarkMode ? '#f0f0f0' : '#333'
              },
              '&:disabled': {
                bgcolor: '#ccc',
                color: '#666'
              }
            }}
            onClick={handleSave}
          >
            {isSaving ? '저장 중...' : '저장하기'}
          </Button>
        </Box>
        </Grid>
      </Grid>

      {/* 상품 선택 Dialog */}
      <Dialog 
        open={productDialogOpen} 
        onClose={() => {
          setProductDialogOpen(false);
          setProductSearchTerm('');
          setSelectedProducts([]);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>상품 선택</DialogTitle>
        <DialogContent>
          {/* 검색 입력 필드 */}
          <TextField
            fullWidth
            placeholder="상품명 또는 상점명으로 검색..."
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>
                  🔍
                </Box>
              ),
            }}
          />
          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">검색된 상품이 없습니다.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                상품 이름 또는 상점 이름을 다시 확인해주세요.
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredProducts.map(product => (
                <ListItem key={product.product_id}>
                  <Checkbox
                    checked={selectedProducts.some(p => p.product_id === product.product_id)}
                    onChange={() => handleSelectProduct(product)}
                  />
                  <ListItemAvatar>
                    <Avatar 
                      src={product.main_images?.[0] || ''} 
                      alt={product.product_name}
                      sx={{ width: 56, height: 56 }}
                    />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={product.product_name}
                    secondary={product.store_name}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {product.price.toLocaleString()}원
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setProductDialogOpen(false);
            setProductSearchTerm('');
            setSelectedProducts([]);
          }} sx={{
            color: isDarkMode ? '#fff' : '#000',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
            }
          }}>
            취소
          </Button>
          <Button 
            onClick={handleAddProducts}
            disabled={selectedProducts.length === 0}
            variant="contained"
            sx={{
              bgcolor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': {
                bgcolor: isDarkMode ? '#f0f0f0' : '#333'
              },
              '&:disabled': {
                bgcolor: '#ccc',
                color: '#666'
              }
            }}
          >
            추가 ({selectedProducts.length}개)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 